import { useEffect, useRef, useState } from 'react'
import { CalendarClock, CalendarOff, CheckCircle2, ChevronDown, Undo2 } from 'lucide-react'
import type { RecurringItem } from '@/api/recurring'
import { Button } from '@/components/ui/button'
import { MenuSurface } from '@/components/ui/menu-surface'
import { cn } from '@/lib/utils'
import type { RecurringActions } from './useRecurringActions'

/**
 * Mark paid plus a caret for Missed / Change date / Undo. Without `label` it is the compact
 * icon button; with one it is a text button. The caller renders `actions.dialogs`.
 */
export function MarkDoneControl({
  item,
  actions,
  label,
  variant = 'outline',
  menuAlign = 'right',
}: {
  item: RecurringItem
  actions: RecurringActions
  label?: string
  variant?: 'primary' | 'outline'
  /** Which edge the menu lines up with; 'left' when the control sits at the left edge. */
  menuAlign?: 'left' | 'right'
}) {
  const [open, setOpen] = useState(false)
  const panelRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const isExpense = item.type === 'EXPENSE'

  useEffect(() => {
    if (!open) return
    function handleOutside(e: MouseEvent) {
      const target = e.target as Node
      if (panelRef.current?.contains(target) || triggerRef.current?.contains(target)) return
      setOpen(false)
    }
    document.addEventListener('mousedown', handleOutside)
    return () => document.removeEventListener('mousedown', handleOutside)
  }, [open])

  const menuItem = (text: string, Icon: typeof CalendarOff, onSelect: () => void) => (
    <button
      type="button"
      role="menuitem"
      onClick={() => {
        setOpen(false)
        onSelect()
      }}
      className="flex min-h-10 w-full items-center gap-2.5 px-3 py-1.5 text-left text-sm text-[var(--fl-text-1)] hover:bg-[var(--fl-surface-hover)]"
    >
      <Icon size={14} aria-hidden="true" />
      {text}
    </button>
  )

  const primary = variant === 'primary'

  return (
    <div className="relative flex items-center" onClick={(e) => e.stopPropagation()}>
      {label ? (
        <Button
          size="sm"
          variant={primary ? 'default' : 'outline'}
          onClick={actions.markPaid}
          disabled={actions.pending}
          aria-label={isExpense ? 'Mark as Paid' : 'Mark as Received'}
          className="h-7 rounded-r-none px-2.5 text-xs font-semibold"
        >
          {label}
        </Button>
      ) : (
        <Button
          variant="ghost"
          size="icon-sm"
          onClick={actions.markPaid}
          disabled={actions.pending}
          tooltip={isExpense ? 'Mark as Paid' : 'Mark as Received'}
          className="text-[var(--fl-income)] hover:bg-[var(--fl-primary-subtle)] hover:text-[var(--fl-income)] rounded-r-none"
        >
          <CheckCircle2 size={16} />
        </Button>
      )}
      <Button
        ref={triggerRef}
        variant={label ? (primary ? 'default' : 'outline') : 'ghost'}
        size="icon-sm"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        tooltip="More options"
        className={cn(
          'rounded-l-none w-5 px-0',
          label ? 'h-7 border-l-0' : 'text-[var(--fl-text-2)]'
        )}
      >
        <ChevronDown size={12} />
      </Button>

      {open && (
        <MenuSurface
          ref={panelRef}
          role="menu"
          className={cn(
            'absolute top-full z-20 mt-1 w-64 py-1',
            menuAlign === 'left' ? 'left-0' : 'right-0'
          )}
          onKeyDown={(e) => {
            if (e.key !== 'Escape') return
            e.stopPropagation()
            setOpen(false)
          }}
        >
          {menuItem('Mark as Missed', CalendarOff, actions.openMissed)}
          {menuItem('Change next due date', CalendarClock, actions.openReschedule)}
          {actions.undoLast &&
            actions.undoLabel &&
            menuItem(actions.undoLabel, Undo2, actions.undoLast)}
        </MenuSurface>
      )}
    </div>
  )
}
